/**
 * SpaceStatusWidget — HF Space + Coordinator status overview
 * Shows: Space stage, coordinator PID, uptime, auto-restart count, Supabase connection
 * Collapsible — click header to expand/collapse
 * Refresh: 30s
 * CSS prefix: ssw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchHealth, fetchWorkerStatus } from '../lib/api'
import { Server, RefreshCw, Wifi, WifiOff, Moon, Zap, ChevronRight } from 'lucide-react'
import './SpaceStatusWidget.css'

function SpaceStatusWidget() {
  const [health, setHealth] = useState(null)
  const [spaceStage, setSpaceStage] = useState('unknown')
  const [status, setStatus] = useState('unknown')
  const [error, setError] = useState(null)
  const [lastCheck, setLastCheck] = useState(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)

    const [healthRes, workerRes] = await Promise.all([
      fetchHealth(),
      fetchWorkerStatus(),
    ])

    if (healthRes.status === 'ok' && healthRes.data) {
      setHealth(healthRes.data)
      setSpaceStage('RUNNING')
      setStatus('online')
      setError(null)
    } else if (healthRes.status === 'sleeping') {
      setSpaceStage('SLEEPING')
      setStatus('sleeping')
      setHealth(null)
      setError(healthRes.message)
    } else if (healthRes.status === 'starting') {
      setSpaceStage(healthRes.hfStage || 'STARTING')
      setStatus('starting')
      setHealth(null)
      setError(healthRes.message)
    } else {
      setStatus('offline')
      setHealth(null)
      setError(healthRes.message)
    }

    if (workerRes.status === 'ok' && workerRes.data?.hf_space_stage) {
      setSpaceStage(workerRes.data.hf_space_stage)
    }

    setLoading(false)
    setLastCheck(new Date().toLocaleTimeString())
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [fetchData])

  const getStageIcon = () => {
    switch (spaceStage) {
      case 'RUNNING': return <Wifi size={18} />
      case 'SLEEPING': case 'PAUSED': case 'STOPPED': return <Moon size={18} />
      case 'BUILDING': case 'RUNNING_APP_STARTING': return <Zap size={18} />
      default: return <WifiOff size={18} />
    }
  }

  const getStatusClass = () => {
    if (status === 'online') return 'online'
    if (status === 'sleeping') return 'sleeping'
    if (status === 'starting') return 'starting'
    return 'offline'
  }

  // Get monitors alive from heartbeat details or restart_state
  const getMonitorsAlive = () => {
    if (!health) return null
    const details = health.last_event?.details
    if (details?.alive !== undefined && details?.total !== undefined) {
      return { alive: details.alive, total: details.total }
    }
    // Fallback: count monitors from restart_state
    const monitors = health.restart_state?.monitors
    if (monitors && typeof monitors === 'object') {
      const total = Object.keys(monitors).length
      return { alive: total, total }
    }
    return null
  }

  const monitorsInfo = getMonitorsAlive()

  return (
    <div className={`widget-card ${getStatusClass()}`}>
      <div className="widget-header ssw-header-clickable" onClick={() => setExpanded(!expanded)}>
        <ChevronRight size={14} className={`ssw-chevron ${expanded ? 'rotated' : ''}`} />
        <Server size={16} className="widget-icon" />
        <span className="widget-title">HF Space Status</span>
        <span className={`status-badge ${getStatusClass()}`}>
          {getStageIcon()}
          {spaceStage}
        </span>
        <button className={`refresh-btn ${loading ? 'spinning' : ''}`} onClick={(e) => { e.stopPropagation(); fetchData() }} disabled={loading}>
          <RefreshCw size={12} />
        </button>
      </div>

      {expanded && (
        <div className="widget-body">
          {status === 'sleeping' && (
            <div className="widget-sleeping">
              <div className="sleeping-icon"><Moon size={32} /></div>
              <div className="sleeping-msg">Space is sleeping</div>
              <div className="sleeping-hint">{error || 'Wake command has been sent. Retrying...'}</div>
            </div>
          )}

          {status === 'starting' && (
            <div className="widget-sleeping">
              <div className="sleeping-icon pulse"><Zap size={32} /></div>
              <div className="sleeping-msg">Space is starting up...</div>
              <div className="sleeping-hint">{error || 'Should be ready in 1-3 minutes.'}</div>
            </div>
          )}

          {status === 'online' && health && (
            <div className="ssw-grid">
              <div className="ssw-stat">
                <div className="ssw-stat-value" style={{ color: 'var(--status-online)' }}>
                  {monitorsInfo
                    ? `${monitorsInfo.alive} of ${monitorsInfo.total}`
                    : (health.events_last_hour > 0 ? 'Active' : '--')
                  }
                </div>
                <div className="ssw-stat-label">Monitors Alive</div>
              </div>
              <div className="ssw-stat">
                <div className="ssw-stat-value">{health.events_last_hour ?? 0}</div>
                <div className="ssw-stat-label">Events / Hour</div>
              </div>
              <div className="ssw-stat">
                <div className="ssw-stat-value" style={{ color: health.crashes_today > 0 ? 'var(--status-error)' : 'var(--status-online)' }}>
                  {health.crashes_today ?? 0}
                </div>
                <div className="ssw-stat-label">Crashes Today</div>
              </div>
              <div className="ssw-stat">
                <div className="ssw-stat-value" style={{ color: health.restarts_today > 0 ? 'var(--status-warning)' : 'var(--status-online)' }}>
                  {health.restarts_today ?? 0}
                </div>
                <div className="ssw-stat-label">Restarts Today</div>
              </div>
            </div>
          )}

          {status === 'offline' && (
            <div className="widget-empty">
              <WifiOff size={24} style={{ marginBottom: 8, opacity: 0.5 }} />
              <div>{error || 'Unable to reach HF Space'}</div>
            </div>
          )}

          <div className="widget-footer">
            <span>{lastCheck ? `Checked: ${lastCheck}` : 'Loading...'}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default SpaceStatusWidget
